import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';

const UNITS      = ['kq', 'qr', 'litr', 'ml', 'ədəd', 'paket', 'qutu', 'dəstə'];
const CATEGORIES = ['Ərzaq', 'Ət məhsulları', 'Tərəvəz/Meyvə', 'İçki', 'Süd məhsulları', 'Ədviyyat', 'Sərfiyyat', 'Digər'];
const CAT_ICONS  = {
  'Ərzaq':'🌾','Ət məhsulları':'🥩','Tərəvəz/Meyvə':'🥦',
  'İçki':'🧃','Süd məhsulları':'🥛','Ədviyyat':'🧂','Sərfiyyat':'🧻','Digər':'📦',
};

function formatDate(str) {
  if (!str) return '—';
  const d = new Date(str);
  return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
}

function exportCSV(purchases, monthLabel) {
  const header = 'Məhsul,Miqdar,Vahid,Vahid Qiymət (₼),Cəmi (₼),Tarix,Satıcı';
  const rows = purchases.map(p => [
    `"${p.inventory_items?.name||''}"`, p.quantity,
    p.inventory_items?.unit||'',
    (p.unit_price||0).toFixed(2),
    (p.total_price||p.quantity*p.unit_price||0).toFixed(2),
    formatDate(p.purchased_at),
    `"${p.supplier||''}"`,
  ].join(','));
  const blob = new Blob(['﻿'+[header,...rows].join('\n')],{type:'text/csv;charset=utf-8;'});
  const a = Object.assign(document.createElement('a'),{href:URL.createObjectURL(blob),download:`anbar-${monthLabel}.csv`});
  a.click(); URL.revokeObjectURL(a.href);
}

// ─── Açılış Sayımı Modal ─────────────────────────────────────────────────────
const EMPTY_ROW = () => ({ name: '', qty: '', unit: 'kq', category: 'Ərzaq' });

function OpeningStockModal({ business, locationId, onClose, onSaved }) {
  const [rows, setRows]     = useState([EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW()]);
  const [saving, setSaving] = useState(false);

  const update = (i, field, val) =>
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));

  const addRow = () => setRows(prev => [...prev, EMPTY_ROW()]);
  const removeRow = i => setRows(prev => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    const valid = rows.filter(r => r.name.trim() && parseFloat(r.qty) > 0);
    if (valid.length === 0) { alert('Ən az bir məhsul adı və miqdar yazın'); return; }
    setSaving(true);
    try {
      for (const r of valid) {
        const { error } = await supabase.rpc('insert_inventory_item', {
          p_business_id: business.id,
          p_location_id: locationId || null,
          p_name:        r.name.trim(),
          p_unit:        r.unit,
          p_category:    r.category,
          p_stock:       parseFloat(r.qty),
        });
        if (error) throw error;
      }
      onSaved();
      onClose();
    } catch (err) {
      alert('Xəta: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex',
      alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:620,
        maxHeight:'90vh', display:'flex', flexDirection:'column',
        boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>

        {/* Başlıq */}
        <div style={{ padding:'18px 22px', borderBottom:'1px solid #f0f0f0',
          display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:17, fontWeight:800, color:'#1a1a2e' }}>📋 Mövcud Stok Girişi</div>
            <div style={{ fontSize:12, color:'#9ca3af', marginTop:3 }}>
              Hazırda anbarda olan malları bir dəfəyə daxil edin
            </div>
          </div>
          <button onClick={onClose} style={{ background:'#f3f4f6', border:'none', borderRadius:8,
            width:32, height:32, cursor:'pointer', fontSize:15 }}>✕</button>
        </div>

        {/* Cədvəl başlığı */}
        <div style={{ display:'flex', gap:8, padding:'10px 22px 6px',
          background:'#f8f9fa', flexShrink:0 }}>
          <div style={{ flex:2, fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase' }}>Məhsul adı</div>
          <div style={{ width:80, fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase' }}>Miqdar</div>
          <div style={{ width:80, fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase' }}>Vahid</div>
          <div style={{ flex:1, fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase' }}>Kateqoriya</div>
          <div style={{ width:28 }} />
        </div>

        {/* Sətirlər */}
        <div style={{ overflowY:'auto', flex:1, padding:'8px 22px' }}>
          {rows.map((row, i) => (
            <div key={i} style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6 }}>
              <input
                value={row.name}
                onChange={e => update(i, 'name', e.target.value)}
                placeholder={`Məhsul ${i + 1} (un, yağ, ət...)`}
                style={{ flex:2, ...inpSm }}
              />
              <input
                type="number" min="0" step="0.1"
                value={row.qty}
                onChange={e => update(i, 'qty', e.target.value)}
                placeholder="0"
                style={{ width:80, ...inpSm }}
              />
              <select value={row.unit} onChange={e => update(i, 'unit', e.target.value)}
                style={{ width:80, ...inpSm }}>
                {UNITS.map(u => <option key={u}>{u}</option>)}
              </select>
              <select value={row.category} onChange={e => update(i, 'category', e.target.value)}
                style={{ flex:1, ...inpSm }}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
              <button onClick={() => removeRow(i)}
                style={{ width:28, height:32, border:'none', background:'#fee2e2',
                  color:'#ef4444', borderRadius:6, cursor:'pointer', fontSize:13, flexShrink:0 }}>
                ✕
              </button>
            </div>
          ))}

          <button onClick={addRow} style={{
            display:'block', width:'100%', marginTop:6, padding:'8px',
            borderRadius:8, border:'1.5px dashed #d1d5db',
            background:'#fafafa', color:'#6b7280',
            fontSize:13, fontWeight:600, cursor:'pointer',
          }}>
            + Sətir əlavə et
          </button>
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 22px', borderTop:'1px solid #f0f0f0', flexShrink:0,
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:12, color:'#9ca3af' }}>
            {rows.filter(r => r.name.trim() && parseFloat(r.qty) > 0).length} məhsul hazırdır
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={onClose} style={{
              padding:'10px 18px', borderRadius:8, border:'1.5px solid #e5e7eb',
              background:'#fff', color:'#6b7280', fontWeight:700, fontSize:13, cursor:'pointer',
            }}>Ləğv</button>
            <button onClick={save} disabled={saving} style={{
              padding:'10px 22px', borderRadius:8, border:'none',
              background: saving ? '#d1d5db' : '#1a1a2e', color:'#fff',
              fontWeight:800, fontSize:14, cursor: saving ? 'not-allowed' : 'pointer',
            }}>
              {saving ? 'Saxlanır...' : '✅ Stoku Qeyd Et'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inpSm = {
  padding:'7px 9px', borderRadius:7, border:'1.5px solid #e5e7eb',
  fontSize:13, boxSizing:'border-box', outline:'none', background:'#fff',
};

// ─── Yeni Alış Modal ──────────────────────────────────────────────────────────
function PurchaseModal({ business, locationId, items, onClose, onSaved, preSelectedItem }) {
  const [itemId,       setItemId]       = useState(preSelectedItem?.id || '');
  const [newItemName,  setNewItemName]  = useState('');
  const [newItemUnit,  setNewItemUnit]  = useState('kq');
  const [newItemCat,   setNewItemCat]   = useState('Ərzaq');
  const [isNewItem,    setIsNewItem]    = useState(false);
  const [quantity,     setQuantity]     = useState('');
  const [unitPrice,    setUnitPrice]    = useState('');
  const [purchasedAt,  setPurchasedAt]  = useState(new Date().toISOString().slice(0,10));
  const [supplier,     setSupplier]     = useState('');
  const [note,         setNote]         = useState('');
  const [saving,       setSaving]       = useState(false);

  const selectedItem = items.find(i => i.id === itemId);
  const activeItem   = preSelectedItem || selectedItem;
  const activeUnit   = activeItem?.unit || (isNewItem ? newItemUnit : '—');
  const total        = quantity && unitPrice ? (parseFloat(quantity)*parseFloat(unitPrice)).toFixed(2) : '0.00';
  const newStock     = preSelectedItem && quantity
    ? (preSelectedItem.current_stock + parseFloat(quantity||0)).toFixed(2) : null;

  const save = async () => {
    if (!quantity || !unitPrice) { alert('Miqdar və qiymət mütləqdir'); return; }
    setSaving(true);
    try {
      let fid = itemId;
      if (isNewItem && !preSelectedItem) {
        if (!newItemName.trim()) { alert('Məhsul adı yazın'); setSaving(false); return; }
        const { data, error } = await supabase.rpc('insert_inventory_item', {
          p_business_id: business.id,
          p_location_id: locationId || null,
          p_name:        newItemName.trim(),
          p_unit:        newItemUnit,
          p_category:    newItemCat,
          p_stock:       parseFloat(quantity),
        });
        if (error) throw error;
        fid = data;
      } else {
        if (!fid) { alert('Məhsul seçin'); setSaving(false); return; }
        const item = items.find(i=>i.id===fid);
        const newStock = parseFloat(((item?.current_stock||0) + parseFloat(quantity)).toFixed(4));
        const { error: stockErr } = await supabase.rpc('update_inventory_stock', {
          p_item_id: fid,
          p_new_stock: newStock,
        });
        if (stockErr) throw stockErr;
      }
      const { error } = await supabase.rpc('insert_inventory_purchase', {
        p_business_id:  business.id,
        p_item_id:      fid,
        p_quantity:     parseFloat(quantity),
        p_unit_price:   parseFloat(unitPrice),
        p_purchased_at: purchasedAt,
        p_supplier:     supplier.trim() || null,
        p_note:         note.trim() || null,
      });
      if (error) throw error;
      onSaved(); onClose();
    } catch(err) { alert('Xəta: '+err.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',
      alignItems:'center',justifyContent:'center',zIndex:1000,padding:16}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:480,
        maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}}>

        <div style={{padding:'16px 22px',borderBottom:'1px solid #f0f0f0',
          display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{fontSize:16,fontWeight:800,color:'#1a1a2e'}}>
            📦 {preSelectedItem ? `"${preSelectedItem.name}" — Alış` : 'Yeni Alış'}
          </div>
          <button onClick={onClose} style={{background:'#f3f4f6',border:'none',borderRadius:8,
            width:32,height:32,cursor:'pointer',fontSize:15}}>✕</button>
        </div>

        <div style={{padding:'18px 22px',display:'flex',flexDirection:'column',gap:14}}>

          {!preSelectedItem && (
            <div>
              <label style={lbl}>Məhsul</label>
              <div style={{display:'flex',gap:8,marginBottom:8}}>
                <button onClick={()=>setIsNewItem(false)}
                  style={{...tabBtn,...(isNewItem?{}:tabBtnActive)}}>Mövcud</button>
                <button onClick={()=>setIsNewItem(true)}
                  style={{...tabBtn,...(isNewItem?tabBtnActive:{})}}>+ Yeni məhsul</button>
              </div>
              {isNewItem ? (
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  <input value={newItemName} onChange={e=>setNewItemName(e.target.value)}
                    placeholder="Məhsul adı (Un, Ət, Yağ...)" style={inp}/>
                  <div style={{display:'flex',gap:8}}>
                    <select value={newItemUnit} onChange={e=>setNewItemUnit(e.target.value)} style={{...inp,flex:1}}>
                      {UNITS.map(u=><option key={u}>{u}</option>)}
                    </select>
                    <select value={newItemCat} onChange={e=>setNewItemCat(e.target.value)} style={{...inp,flex:2}}>
                      {CATEGORIES.map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              ) : (
                <select value={itemId} onChange={e=>setItemId(e.target.value)} style={inp}>
                  <option value=''>— Məhsul seçin —</option>
                  {items.map(i=><option key={i.id} value={i.id}>{i.name} ({i.unit}) — stok: {i.current_stock}</option>)}
                </select>
              )}
            </div>
          )}

          {preSelectedItem && (
            <div style={{background:'#f8f9fa',borderRadius:10,padding:'10px 14px',
              display:'flex',justifyContent:'space-between',alignItems:'center',border:'1.5px solid #e5e7eb'}}>
              <span style={{fontSize:13,color:'#6b7280'}}>Cari stok:</span>
              <span style={{fontSize:16,fontWeight:800,color:'#1a1a2e'}}>
                {preSelectedItem.current_stock} {preSelectedItem.unit}
              </span>
            </div>
          )}

          <div style={{display:'flex',gap:10}}>
            <div style={{flex:1}}>
              <label style={lbl}>Miqdar ({activeUnit})</label>
              <input type="number" min="0" step="0.01" value={quantity}
                onChange={e=>setQuantity(e.target.value)} placeholder="0.00" style={inp}
                autoFocus={!!preSelectedItem}/>
            </div>
            <div style={{flex:1}}>
              <label style={lbl}>Vahid Qiymət (₼)</label>
              <input type="number" min="0" step="0.01" value={unitPrice}
                onChange={e=>setUnitPrice(e.target.value)} placeholder="0.00" style={inp}/>
            </div>
          </div>

          {newStock !== null && quantity && (
            <div style={{background:'#f0fdf4',borderRadius:10,padding:'10px 14px',
              display:'flex',justifyContent:'space-between',border:'1px solid #bbf7d0'}}>
              <span style={{fontSize:13,color:'#166534'}}>Yeni stok olacaq:</span>
              <span style={{fontSize:15,fontWeight:800,color:'#16a34a'}}>{newStock} {preSelectedItem.unit}</span>
            </div>
          )}

          <div style={{background:'#eff6ff',borderRadius:10,padding:'10px 14px',
            display:'flex',justifyContent:'space-between',border:'1px solid #bfdbfe'}}>
            <span style={{fontSize:13,fontWeight:700,color:'#1d4ed8'}}>Cəmi:</span>
            <span style={{fontSize:16,fontWeight:800,color:'#1d4ed8'}}>{total} ₼</span>
          </div>

          <div style={{display:'flex',gap:10}}>
            <div style={{flex:1}}>
              <label style={lbl}>Alış tarixi</label>
              <input type="date" value={purchasedAt} onChange={e=>setPurchasedAt(e.target.value)} style={inp}/>
            </div>
            <div style={{flex:1}}>
              <label style={lbl}>Satıcı (ixtiyari)</label>
              <input value={supplier} onChange={e=>setSupplier(e.target.value)}
                placeholder="Bazar, Market..." style={inp}/>
            </div>
          </div>

          <div>
            <label style={lbl}>Qeyd (ixtiyari)</label>
            <input value={note} onChange={e=>setNote(e.target.value)} placeholder="Əlavə məlumat..." style={inp}/>
          </div>

          <button onClick={save} disabled={saving} style={{
            padding:'12px',borderRadius:10,border:'none',
            background:saving?'#d1d5db':'#1a1a2e',color:'#fff',
            fontWeight:800,fontSize:15,cursor:saving?'not-allowed':'pointer',marginTop:4,
          }}>
            {saving?'Saxlanır...':'✅ Alışı Qeyd Et'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Redaktə Modal ────────────────────────────────────────────────────────────
function EditItemModal({ item, onClose, onSaved }) {
  const [name,     setName]     = useState(item.name);
  const [category, setCategory] = useState(item.category||'Ərzaq');
  const [unit,     setUnit]     = useState(item.unit||'kq');
  const [minStock, setMinStock] = useState(String(item.min_stock||0));
  const [stock,    setStock]    = useState(String(item.current_stock||0));
  const [saving,   setSaving]   = useState(false);

  const save = async () => {
    if (!name.trim()) { alert('Məhsul adı yazın'); return; }
    setSaving(true);
    const {error} = await supabase.rpc('update_inventory_item', {
      p_item_id:   item.id,
      p_name:      name.trim(),
      p_category:  category,
      p_unit:      unit,
      p_stock:     parseFloat(stock)||0,
      p_min_stock: parseFloat(minStock)||0,
    });
    if (error) { alert(error.message); setSaving(false); return; }
    onSaved(); onClose();
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',
      alignItems:'center',justifyContent:'center',zIndex:1001,padding:16}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:'#fff',borderRadius:18,padding:24,width:'100%',maxWidth:380,
        boxShadow:'0 10px 40px rgba(0,0,0,0.2)'}}>
        <div style={{fontSize:16,fontWeight:800,marginBottom:18,color:'#1a1a2e'}}>✏️ Məhsulu Redaktə Et</div>
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <div>
            <label style={lbl}>Məhsul adı</label>
            <input value={name} onChange={e=>setName(e.target.value)} style={inp} autoFocus/>
          </div>
          <div style={{display:'flex',gap:8}}>
            <div style={{flex:1}}>
              <label style={lbl}>Vahid</label>
              <select value={unit} onChange={e=>setUnit(e.target.value)} style={inp}>
                {UNITS.map(u=><option key={u}>{u}</option>)}
              </select>
            </div>
            <div style={{flex:2}}>
              <label style={lbl}>Kateqoriya</label>
              <select value={category} onChange={e=>setCategory(e.target.value)} style={inp}>
                {CATEGORIES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <div style={{flex:1}}>
              <label style={lbl}>Cari stok ({unit})</label>
              <input type="number" min="0" step="0.01" value={stock}
                onChange={e=>setStock(e.target.value)} style={inp}/>
            </div>
            <div style={{flex:1}}>
              <label style={lbl}>Min hədd ({unit})</label>
              <input type="number" min="0" step="0.01" value={minStock}
                onChange={e=>setMinStock(e.target.value)} style={inp}/>
            </div>
          </div>
          <p style={{fontSize:12,color:'#9ca3af',margin:0}}>
            Min həddən aşağı düşdükdə ⚠️ xəbərdarlıq göstərilir.
          </p>
        </div>
        <div style={{display:'flex',gap:8,marginTop:18}}>
          <button onClick={save} disabled={saving} style={{
            flex:1,padding:'11px',borderRadius:8,border:'none',
            background:saving?'#d1d5db':'#1a1a2e',color:'#fff',fontWeight:800,cursor:'pointer',fontSize:14,
          }}>{saving?'...':'Saxla'}</button>
          <button onClick={onClose} style={{
            padding:'11px 16px',borderRadius:8,border:'1.5px solid #e5e7eb',
            background:'#fff',color:'#6b7280',fontWeight:700,cursor:'pointer',fontSize:14,
          }}>Ləğv</button>
        </div>
      </div>
    </div>
  );
}

// ─── Məhsul Tarixçəsi Modal ───────────────────────────────────────────────────
function ItemDetailModal({ item, purchases, onClose, onQuickAdd, onEdit }) {
  const itemPurchases = purchases
    .filter(p=>p.item_id===item.id)
    .sort((a,b)=>new Date(b.purchased_at)-new Date(a.purchased_at));

  const totalSpent  = itemPurchases.reduce((s,p)=>s+(p.total_price||p.quantity*p.unit_price||0),0);
  const totalBought = itemPurchases.reduce((s,p)=>s+(p.quantity||0),0);
  const avgPrice    = itemPurchases.length
    ? itemPurchases.reduce((s,p)=>s+(p.unit_price||0),0)/itemPurchases.length : 0;
  const isLow = item.min_stock>0 && item.current_stock<=item.min_stock;

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',
      alignItems:'center',justifyContent:'center',zIndex:1000,padding:16}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:520,
        maxHeight:'88vh',display:'flex',flexDirection:'column',
        boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}}>

        {/* Başlıq */}
        <div style={{padding:'16px 22px',borderBottom:'1px solid #f0f0f0'}}>
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:12}}>
            <div style={{width:48,height:48,borderRadius:13,flexShrink:0,
              background:isLow?'#fef2f2':'#f8f9fa',
              display:'flex',alignItems:'center',justifyContent:'center',fontSize:24}}>
              {CAT_ICONS[item.category]||'📦'}
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:18,fontWeight:800,color:'#1a1a2e'}}>{item.name}</div>
              <div style={{fontSize:12,color:'#9ca3af',marginTop:1}}>{item.category||'—'} · {item.unit}</div>
            </div>
            <button onClick={onClose} style={{background:'#f3f4f6',border:'none',borderRadius:8,
              width:34,height:34,cursor:'pointer',fontSize:15,flexShrink:0}}>✕</button>
          </div>

          {/* 4 stat */}
          <div style={{display:'flex',borderRadius:12,overflow:'hidden',border:'1.5px solid #f0f0f0'}}>
            {[
              {label:'Cari stok', value:`${item.current_stock} ${item.unit}`, accent:isLow?'#dc2626':undefined},
              {label:'Ümumi alınan', value:`${totalBought} ${item.unit}`},
              {label:'Ort. qiymət', value:`${avgPrice.toFixed(2)} ₼`},
              {label:'Ümumi xərc', value:`${totalSpent.toFixed(2)} ₼`},
            ].map((s,i)=>(
              <div key={i} style={{flex:1,padding:'10px 8px',textAlign:'center',
                borderRight:i<3?'1px solid #f0f0f0':'none',background:i===0&&isLow?'#fef2f2':'#fff'}}>
                <div style={{fontSize:14,fontWeight:800,color:s.accent||'#1a1a2e'}}>{s.value}</div>
                <div style={{fontSize:10,color:'#9ca3af',marginTop:1}}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{display:'flex',gap:8,marginTop:10}}>
            <button onClick={()=>{onClose();onQuickAdd(item);}} style={{
              flex:1,padding:'9px',borderRadius:9,border:'none',
              background:'#1a1a2e',color:'#fff',fontWeight:700,fontSize:14,cursor:'pointer',
            }}>+ Alış əlavə et</button>
            <button onClick={()=>{onClose();onEdit(item);}} style={{
              padding:'9px 14px',borderRadius:9,border:'1.5px solid #e5e7eb',
              background:'#fff',color:'#6b7280',fontWeight:700,fontSize:13,cursor:'pointer',
            }}>✏️ Redaktə</button>
          </div>
        </div>

        {/* Siyahı */}
        <div style={{overflowY:'auto',flex:1,padding:'14px 22px',display:'flex',flexDirection:'column',gap:8}}>
          <div style={{fontSize:13,fontWeight:700,color:'#6b7280',marginBottom:2}}>
            Alış tarixçəsi
          </div>
          {itemPurchases.length===0 ? (
            <div style={{textAlign:'center',padding:40}}>
              <div style={{fontSize:36,marginBottom:8}}>📋</div>
              <div style={{fontSize:14,color:'#9ca3af'}}>Hələ alış qeyd edilməyib</div>
            </div>
          ) : itemPurchases.map((p,idx)=>{
            const total = p.total_price||p.quantity*p.unit_price||0;
            return (
              <div key={p.id} style={{display:'flex',alignItems:'center',gap:12,
                padding:'11px 14px',borderRadius:10,
                background:idx===0?'#f8faff':'#fafafa',
                border:idx===0?'1.5px solid #dbeafe':'1.5px solid #f0f0f0'}}>
                <div style={{textAlign:'center',minWidth:48,flexShrink:0}}>
                  <div style={{fontSize:17,fontWeight:800,color:'#1a1a2e',lineHeight:1}}>
                    {new Date(p.purchased_at).getDate().toString().padStart(2,'0')}
                  </div>
                  <div style={{fontSize:10,color:'#9ca3af',textTransform:'uppercase'}}>
                    {new Date(p.purchased_at).toLocaleString('az',{month:'short'})}{' '}
                    {new Date(p.purchased_at).getFullYear()}
                  </div>
                </div>
                <div style={{width:1,height:34,background:'#e5e7eb',flexShrink:0}}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:700,color:'#1a1a2e'}}>
                    {p.quantity} {item.unit}
                    <span style={{fontSize:12,fontWeight:500,color:'#6b7280',marginLeft:6}}>
                      × {p.unit_price?.toFixed(2)}₼
                    </span>
                    {idx===0&&<span style={{marginLeft:8,fontSize:10,background:'#dbeafe',
                      color:'#1d4ed8',padding:'1px 6px',borderRadius:4,fontWeight:700}}>Son alış</span>}
                  </div>
                  <div style={{fontSize:11,color:'#9ca3af',marginTop:2}}>
                    {p.supplier?`📍 ${p.supplier}`:''}{p.note?` · "${p.note}"`:''}{!p.supplier&&!p.note?'—':''}
                  </div>
                </div>
                <div style={{textAlign:'right',flexShrink:0}}>
                  <div style={{fontSize:14,fontWeight:800,color:'#1a1a2e'}}>{total.toFixed(2)} ₼</div>
                </div>
              </div>
            );
          })}
        </div>

        {itemPurchases.length>0&&(
          <div style={{padding:'10px 22px',borderTop:'1px solid #f0f0f0',
            display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:12,color:'#9ca3af'}}>{itemPurchases.length} alış əməliyyatı</span>
            <span style={{fontSize:14,fontWeight:800,color:'#1a1a2e'}}>Cəmi: {totalSpent.toFixed(2)} ₼</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Ana InventoryPage ────────────────────────────────────────────────────────
export default function InventoryPage() {
  const { business }   = useAuth();
  const { locationId } = useLocation();

  const [items,       setItems]       = useState([]);
  const [purchases,   setPurchases]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState('stock');
  const [showModal,   setShowModal]   = useState(false);
  const [quickAddItem,setQuickAddItem]= useState(null);
  const [editModal,    setEditModal]   = useState(null);
  const [detailItem,   setDetailItem]  = useState(null);
  const [openingStock, setOpeningStock] = useState(false);
  const [filterCat,   setFilterCat]   = useState('');
  const [searchStock, setSearchStock] = useState('');
  const [sortBy,      setSortBy]      = useState('name');
  const [monthFilter, setMonthFilter] = useState(new Date().toISOString().slice(0,7));

  const load = useCallback(async () => {
    if (!business?.id) return;
    setLoading(true);
    const [ir, pr] = await Promise.all([
      supabase.from('inventory_items').select('*').eq('business_id',business.id).order('name'),
      supabase.from('inventory_purchases').select('*, inventory_items(name,unit)')
        .eq('business_id',business.id).order('purchased_at',{ascending:false}).limit(300),
    ]);
    setItems(ir.data||[]); setPurchases(pr.data||[]); setLoading(false);
  }, [business?.id]);

  useEffect(()=>{load();},[load]);

  const deleteItem = async id => {
    if (!window.confirm('Bu məhsulu silmək istəyirsiniz?')) return;
    const { error } = await supabase.rpc('delete_inventory_item', { p_item_id: id });
    if (error) { alert('Silinmə xətası: ' + error.message); return; }
    load();
  };

  const lowStock = items.filter(i=>i.min_stock>0&&i.current_stock<=i.min_stock);

  const lastPrices = useMemo(()=>{
    const m={};
    [...purchases].reverse().forEach(p=>{m[p.item_id]=p.unit_price;});
    return m;
  },[purchases]);

  const estimatedValue = useMemo(()=>
    items.reduce((s,i)=>s+i.current_stock*(lastPrices[i.id]||0),0),[items,lastPrices]);

  const thisMonth = new Date().toISOString().slice(0,7);
  const thisMonthTotal = useMemo(()=>
    purchases.filter(p=>p.purchased_at?.startsWith(thisMonth))
      .reduce((s,p)=>s+(p.total_price||p.quantity*p.unit_price||0),0),[purchases,thisMonth]);

  const filteredAndSorted = useMemo(()=>{
    let list = filterCat ? items.filter(i=>i.category===filterCat) : items;
    if (searchStock.trim()) {
      const q=searchStock.toLowerCase();
      list=list.filter(i=>i.name.toLowerCase().includes(q)||(i.category||'').toLowerCase().includes(q));
    }
    list=[...list];
    if (sortBy==='name')       list.sort((a,b)=>a.name.localeCompare(b.name));
    if (sortBy==='stock_asc')  list.sort((a,b)=>a.current_stock-b.current_stock);
    if (sortBy==='stock_desc') list.sort((a,b)=>b.current_stock-a.current_stock);
    if (sortBy==='low_first')  list.sort((a,b)=>{
      const aL=a.min_stock>0&&a.current_stock<=a.min_stock;
      const bL=b.min_stock>0&&b.current_stock<=b.min_stock;
      return (bL?1:0)-(aL?1:0);
    });
    return list;
  },[items,filterCat,searchStock,sortBy]);

  const filteredPurchases = useMemo(()=>
    purchases.filter(p=>p.purchased_at?.startsWith(monthFilter)),[purchases,monthFilter]);
  const monthTotal = filteredPurchases.reduce((s,p)=>s+(p.total_price||p.quantity*p.unit_price||0),0);
  const allCats = [...new Set(items.map(i=>i.category).filter(Boolean))];

  if (loading) return (
    <div style={{display:'flex',justifyContent:'center',padding:60}}>
      <div style={{fontSize:15,color:'#9ca3af'}}>Yüklənir...</div>
    </div>
  );

  return (
    <div style={{padding:'24px 20px',maxWidth:900,margin:'0 auto'}}>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        marginBottom:20,flexWrap:'wrap',gap:12}}>
        <div>
          <h1 style={{fontSize:22,fontWeight:800,color:'#1a1a2e',margin:0}}>📦 Anbar</h1>
          <p style={{fontSize:13,color:'#6b7280',margin:'4px 0 0'}}>Stok izləmə və alış tarixçəsi</p>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          {items.length === 0 && (
            <button onClick={() => setOpeningStock(true)} style={{
              padding:'10px 18px', borderRadius:10, border:'1.5px solid #6366f1',
              background:'#eef2ff', color:'#4f46e5', fontWeight:700, fontSize:14, cursor:'pointer',
            }}>📋 Mövcud Stoku Daxil Et</button>
          )}
          <button onClick={()=>setShowModal(true)} style={{
            padding:'10px 20px',borderRadius:10,border:'none',
            background:'#1a1a2e',color:'#fff',fontWeight:700,fontSize:14,cursor:'pointer',
          }}>+ Yeni Alış</button>
          <div style={{display:'flex',gap:6}}>
            {[{key:'stock',label:'Stok'},{key:'history',label:'Tarixçə'}].map(t=>(
              <button key={t.key} onClick={()=>setTab(t.key)} style={{
                padding:'8px 18px',borderRadius:8,
                border:`1.5px solid ${tab===t.key?'#1a1a2e':'#e5e7eb'}`,
                background:tab===t.key?'#1a1a2e':'#fff',
                color:tab===t.key?'#fff':'#6b7280',
                fontWeight:700,fontSize:13,cursor:'pointer',
              }}>{t.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Stat kartlar */}
      <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap'}}>
        {[
          {icon:'📦',label:'Ümumi məhsul',value:items.length,sub:`${allCats.length} kateqoriya`},
          {icon:'⚠️',label:'Aşağı stok',value:lowStock.length,
            accent:lowStock.length>0?'#dc2626':'#16a34a',
            sub:lowStock.length>0?'diqqət lazımdır':'hər şey normaldır'},
          {icon:'💸',label:'Bu ay xərc',value:`${thisMonthTotal.toFixed(2)} ₼`,sub:'alış xərcləri'},
          {icon:'💰',label:'Stok dəyəri',value:`${estimatedValue.toFixed(2)} ₼`,sub:'son qiymətə görə'},
        ].map((s,i)=>(
          <div key={i} style={{background:'#fff',borderRadius:12,padding:'12px 16px',
            boxShadow:'0 1px 6px rgba(0,0,0,0.06)',border:'1.5px solid #f0f0f0',flex:1,minWidth:120}}>
            <div style={{fontSize:20,marginBottom:5}}>{s.icon}</div>
            <div style={{fontSize:18,fontWeight:800,color:s.accent||'#1a1a2e'}}>{s.value}</div>
            <div style={{fontSize:11,color:'#9ca3af',marginTop:1}}>{s.label}</div>
            {s.sub&&<div style={{fontSize:11,color:'#6b7280',marginTop:1}}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* Aşağı stok xəbərdarlığı */}
      {lowStock.length>0&&(
        <div style={{background:'#fef2f2',borderRadius:12,padding:'12px 16px',marginBottom:20,
          border:'1.5px solid #fecaca',display:'flex',alignItems:'flex-start',gap:10}}>
          <span style={{fontSize:18}}>⚠️</span>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,fontSize:13,color:'#991b1b',marginBottom:6}}>
              {lowStock.length} məhsulun stoku kritik həddədir
            </div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {lowStock.map(i=>(
                <button key={i.id} onClick={()=>setQuickAddItem(i)} style={{
                  padding:'4px 12px',borderRadius:20,border:'1px solid #fca5a5',
                  background:'#fff',color:'#dc2626',fontSize:12,fontWeight:600,cursor:'pointer',
                }}>{i.name} ({i.current_stock} {i.unit}) + Al</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ STOK TAB ══════════════════════════════════════════════════════════ */}
      {tab==='stock'&&(
        <>
          <div style={{display:'flex',gap:10,marginBottom:12,flexWrap:'wrap'}}>
            <input value={searchStock} onChange={e=>setSearchStock(e.target.value)}
              placeholder="🔍 Məhsul axtar..."
              style={{...inp,flex:1,minWidth:160,maxWidth:260}}/>
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
              style={{...inp,width:'auto',minWidth:150}}>
              <option value="name">Ada görə A→Z</option>
              <option value="stock_asc">Stok: az → çox</option>
              <option value="stock_desc">Stok: çox → az</option>
              <option value="low_first">Əvvəl aşağı stok</option>
            </select>
          </div>

          {allCats.length>1&&(
            <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
              <button onClick={()=>setFilterCat('')}
                style={{...chip,...(filterCat===''?chipActive:{})}}>Hamısı ({items.length})</button>
              {allCats.map(c=>(
                <button key={c} onClick={()=>setFilterCat(c)}
                  style={{...chip,...(filterCat===c?chipActive:{})}}>
                  {CAT_ICONS[c]||'📦'} {c} ({items.filter(i=>i.category===c).length})
                </button>
              ))}
            </div>
          )}

          {filteredAndSorted.length===0?(
            <div style={{textAlign:'center',padding:60}}>
              <div style={{fontSize:40,marginBottom:12}}>📦</div>
              <div style={{fontSize:15,color:'#9ca3af',fontWeight:600}}>
                {searchStock?'Nəticə tapılmadı':'Stok boşdur'}
              </div>
              {!searchStock&&(
                <>
                  <div style={{fontSize:13,color:'#d1d5db',marginTop:6,marginBottom:18}}>
                    Sistemə ilk daxil olursunuzsa aşağıdakı düyməni istifadə edin
                  </div>
                  <button onClick={()=>setOpeningStock(true)} style={{
                    padding:'12px 24px', borderRadius:10, border:'2px solid #6366f1',
                    background:'#eef2ff', color:'#4f46e5', fontWeight:700, fontSize:14, cursor:'pointer',
                  }}>
                    📋 Mövcud Stoku Daxil Et
                  </button>
                </>
              )}
            </div>
          ):(
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {filteredAndSorted.map(item=>{
                const isLow  = item.min_stock>0&&item.current_stock<=item.min_stock;
                const isWarn = !isLow&&item.min_stock>0&&item.current_stock<=item.min_stock*2;
                const lastPrice    = lastPrices[item.id];
                const lastPurchase = purchases.find(p=>p.item_id===item.id);
                const progressMax  = item.min_stock>0?item.min_stock*3:null;
                const progressPct  = progressMax?Math.min((item.current_stock/progressMax)*100,100):null;

                return (
                  <div key={item.id}
                    onClick={()=>setDetailItem(item)}
                    style={{background:'#fff',borderRadius:13,padding:'14px 18px',
                      boxShadow:'0 1px 6px rgba(0,0,0,0.06)',cursor:'pointer',
                      border:isLow?'2px solid #fca5a5':isWarn?'2px solid #fed7aa':'1.5px solid #f0f0f0',
                      transition:'box-shadow 0.15s'}}
                    onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.11)'}
                    onMouseLeave={e=>e.currentTarget.style.boxShadow='0 1px 6px rgba(0,0,0,0.06)'}>

                    <div style={{display:'flex',alignItems:'center',gap:12}}>
                      <div style={{width:46,height:46,borderRadius:12,flexShrink:0,
                        background:isLow?'#fef2f2':isWarn?'#fffbeb':'#f8f9fa',
                        display:'flex',alignItems:'center',justifyContent:'center',fontSize:22}}>
                        {CAT_ICONS[item.category]||'📦'}
                      </div>

                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:15,fontWeight:700,color:'#1a1a2e'}}>{item.name}</div>
                        <div style={{fontSize:12,color:'#9ca3af',marginTop:2}}>
                          {item.category||'—'}
                          {lastPurchase&&<span style={{color:'#cbd5e1'}}>
                            {' · '}Son: {formatDate(lastPurchase.purchased_at)}
                            {lastPrice?` · ${lastPrice.toFixed(2)}₼/${item.unit}`:''}
                          </span>}
                        </div>
                      </div>

                      <div style={{textAlign:'center',minWidth:64,flexShrink:0}}>
                        <div style={{fontSize:20,fontWeight:800,color:isLow?'#dc2626':'#1a1a2e',lineHeight:1}}>
                          {item.current_stock}
                        </div>
                        <div style={{fontSize:11,color:'#9ca3af'}}>{item.unit}</div>
                      </div>

                      {item.min_stock>0&&(
                        <div style={{fontSize:11,fontWeight:600,whiteSpace:'nowrap',flexShrink:0,
                          color:isLow?'#dc2626':'#6b7280',
                          background:isLow?'#fef2f2':'#f3f4f6',
                          padding:'3px 8px',borderRadius:6}}>
                          min: {item.min_stock} {item.unit}
                        </div>
                      )}

                      <div style={{display:'flex',gap:6}} onClick={e=>e.stopPropagation()}>
                        <button onClick={()=>setQuickAddItem(item)} style={{
                          padding:'7px 14px',borderRadius:8,border:'none',
                          background:'#1a1a2e',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',
                        }}>+ Al</button>
                        <button onClick={()=>setEditModal(item)} style={{
                          padding:'7px 10px',borderRadius:8,border:'1.5px solid #e5e7eb',
                          background:'#fff',color:'#6b7280',fontSize:13,cursor:'pointer',
                        }}>✏️</button>
                        <button onClick={()=>deleteItem(item.id)} style={{
                          padding:'7px 10px',borderRadius:8,border:'none',
                          background:'#fee2e2',color:'#ef4444',fontSize:13,cursor:'pointer',
                        }}>🗑️</button>
                      </div>
                    </div>

                    {progressPct!==null&&(
                      <div style={{marginTop:10}}>
                        <div style={{height:5,borderRadius:3,background:'#f0f0f0',overflow:'hidden'}}>
                          <div style={{height:'100%',borderRadius:3,transition:'width 0.3s',
                            width:`${progressPct}%`,
                            background:isLow?'#ef4444':isWarn?'#f59e0b':'#22c55e'}}/>
                        </div>
                        <div style={{fontSize:10,color:'#9ca3af',marginTop:3,
                          display:'flex',justifyContent:'space-between'}}>
                          <span>{isLow?'🔴 Kritik':isWarn?'🟡 Az':'🟢 Normal'}</span>
                          <span>{item.current_stock} / {progressMax} {item.unit}
                            {lastPrice&&<span style={{marginLeft:8,color:'#374151',fontWeight:600}}>
                              ≈ {(item.current_stock*lastPrice).toFixed(2)} ₼
                            </span>}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ═══ TARİXÇƏ TAB ═══════════════════════════════════════════════════════ */}
      {tab==='history'&&(
        <>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,flexWrap:'wrap'}}>
            <input type="month" value={monthFilter} onChange={e=>setMonthFilter(e.target.value)}
              style={{padding:'8px 12px',borderRadius:8,border:'1.5px solid #e5e7eb',fontSize:14}}/>
            <div style={{padding:'8px 16px',borderRadius:8,background:'#f0fdf4',
              border:'1px solid #bbf7d0',fontWeight:700,fontSize:14,color:'#166534',whiteSpace:'nowrap'}}>
              {monthTotal.toFixed(2)} ₼
            </div>
            <div style={{fontSize:12,color:'#9ca3af',whiteSpace:'nowrap'}}>
              {filteredPurchases.length} əməliyyat
            </div>
            <button onClick={()=>exportCSV(filteredPurchases,monthFilter)} style={{
              padding:'8px 14px',borderRadius:8,border:'1.5px solid #e5e7eb',
              background:'#fff',color:'#6b7280',fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',
            }}>📥 CSV</button>
          </div>

          {filteredPurchases.length===0?(
            <div style={{textAlign:'center',padding:60}}>
              <div style={{fontSize:40,marginBottom:12}}>📋</div>
              <div style={{fontSize:15,color:'#9ca3af',fontWeight:600}}>Bu ayda alış yoxdur</div>
            </div>
          ):(
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {filteredPurchases.map(p=>{
                const relatedItem=items.find(i=>i.id===p.item_id);
                const total=p.total_price||p.quantity*p.unit_price||0;
                return (
                  <div key={p.id} style={{background:'#fff',borderRadius:12,padding:'12px 18px',
                    boxShadow:'0 1px 4px rgba(0,0,0,0.05)',border:'1.5px solid #f0f0f0',
                    display:'flex',alignItems:'center',gap:12}}>
                    <div style={{fontSize:22,width:34,textAlign:'center',flexShrink:0}}>
                      {CAT_ICONS[relatedItem?.category]||'📦'}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:700,color:'#1a1a2e'}}>
                        {p.inventory_items?.name||'—'}
                      </div>
                      <div style={{fontSize:12,color:'#9ca3af',marginTop:2}}>
                        {p.quantity} {p.inventory_items?.unit}
                        {p.supplier?` · ${p.supplier}`:''}
                        {p.note?` · ${p.note}`:''}
                      </div>
                    </div>
                    <div style={{textAlign:'right',flexShrink:0}}>
                      <div style={{fontSize:14,fontWeight:800,color:'#1a1a2e'}}>{total.toFixed(2)} ₼</div>
                      <div style={{fontSize:11,color:'#9ca3af'}}>
                        {p.unit_price?.toFixed(2)}₼/{p.inventory_items?.unit} · {formatDate(p.purchased_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div style={{background:'#f8f9fa',borderRadius:12,padding:'12px 18px',
                border:'1.5px solid #e5e7eb',marginTop:4,
                display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span style={{fontSize:13,fontWeight:700,color:'#6b7280'}}>
                  {filteredPurchases.length} əməliyyat · {monthFilter}
                </span>
                <span style={{fontSize:15,fontWeight:800,color:'#1a1a2e'}}>
                  Cəmi: {monthTotal.toFixed(2)} ₼
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {openingStock && (
        <OpeningStockModal
          business={business} locationId={locationId}
          onClose={() => setOpeningStock(false)} onSaved={load} />
      )}
      {detailItem&&<ItemDetailModal item={detailItem} purchases={purchases}
        onClose={()=>setDetailItem(null)} onQuickAdd={setQuickAddItem} onEdit={setEditModal}/>}
      {showModal&&<PurchaseModal business={business} locationId={locationId} items={items}
        onClose={()=>setShowModal(false)} onSaved={load}/>}
      {quickAddItem&&<PurchaseModal business={business} locationId={locationId} items={items}
        onClose={()=>setQuickAddItem(null)} onSaved={()=>{load();setQuickAddItem(null);}}
        preSelectedItem={quickAddItem}/>}
      {editModal&&<EditItemModal item={editModal}
        onClose={()=>setEditModal(null)} onSaved={load}/>}
    </div>
  );
}

// ─── Stillər ─────────────────────────────────────────────────────────────────
const lbl = {fontSize:13,fontWeight:700,color:'#374151',display:'block',marginBottom:5};
const inp = {width:'100%',padding:'9px 12px',borderRadius:8,border:'1.5px solid #e5e7eb',
  fontSize:14,boxSizing:'border-box',outline:'none',background:'#fff'};
const chip = {padding:'6px 14px',borderRadius:20,border:'1.5px solid #e5e7eb',
  background:'#fff',color:'#6b7280',fontSize:12,fontWeight:600,cursor:'pointer'};
const chipActive = {borderColor:'#1a1a2e',background:'#1a1a2e',color:'#fff'};
const tabBtn = {flex:1,padding:'7px 12px',borderRadius:8,border:'1.5px solid #e5e7eb',
  background:'#fff',color:'#6b7280',fontSize:13,fontWeight:600,cursor:'pointer'};
const tabBtnActive = {border:'1.5px solid #1a1a2e',background:'#1a1a2e',color:'#fff'};
